import { type AnalyticsEventPayload } from '@atlaskit/analytics-next';
import Flag, { FlagGroup } from '@atlaskit/flag';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';
import { useGasv3Listener } from '@atlassian/post-office-context';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';
import { useFireEngagementPlatformAnalyticsEvents } from '@post-office/analytics';
import { useEffect } from 'react';

import { type FormattedMessages } from '../../../formatted-messages';

export type PlacementRenderProps = {
	onDismiss: () => void;
	trigger: AnalyticsEventPayload;
	primaryActionDataUrl: string;
	messageId: string;
	formattedMessages: FormattedMessages;
	userId?: string;
	tenantId?: string;
	locale?: string;
};

export const EPFlagComponent = ({
	onDismiss,
	trigger,
	primaryActionDataUrl,
	messageId,
	formattedMessages,
	userId,
	tenantId,
	locale,
}: PlacementRenderProps) => {
	const showMessage = useGasv3Listener(trigger);
	const { fireAnalyticsEvent } = useFireEngagementPlatformAnalyticsEvents();
	const { messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (showMessage) {
			void messageInstanceViewed();
			if (userId && tenantId) {
				fireAnalyticsEvent({
					messageType: 'inapp',
					messageId: 'INAPP-3282',
					userId,
					tenantId,
					locale,
				});
			}
		}
	}, [fireAnalyticsEvent, tenantId, userId, locale, showMessage, messageInstanceViewed]);

	return (
		<ManualMessageViewedEvent>
			{showMessage ? (
				<EPFlagComponentDisplay
					onDismiss={onDismiss}
					trigger={trigger}
					primaryActionDataUrl={primaryActionDataUrl}
					messageId={messageId}
					formattedMessages={formattedMessages}
				/>
			) : null}
		</ManualMessageViewedEvent>
	);
};

/* We isolate the display in order to visualize on Storybook easily without needed to mock the useGasv3Listener */
export const EPFlagComponentDisplay = ({
	onDismiss,
	primaryActionDataUrl,
	messageId,
	formattedMessages,
}: PlacementRenderProps) => {
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const handleDismissClick = () => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: { actionType: 'dismiss' },
			additionalMetadataFromFrontend: {},
		});
		onDismiss();
	};

	return (
		<FlagGroup onDismissed={handleDismissClick}>
			<Flag
				title={formattedMessages.title}
				description={formattedMessages.description}
				icon={<StatusInformationIcon color={token('color.icon.information')} label="Info" />}
				id={messageId}
				actions={[
					{
						content: formattedMessages.primaryButton,
						href: primaryActionDataUrl,
						target: '_blank',
					},
					...(formattedMessages.secondaryButton
						? [
								{
									content: formattedMessages.secondaryButton,
									onClick: handleDismissClick,
								},
							]
						: []),
				]}
			/>
		</FlagGroup>
	);
};
